import { useEffect, useState } from "react";
import {
  changePassword,
  confirmPasswordReset,
  getCurrentSession,
  getDashboard,
  getIncidents,
  getNotifications,
  getWorkspaces,
  login,
  logout,
  markNotificationRead,
  requestPasswordReset,
  register,
  resendVerification,
  switchWorkspace,
  verifyEmail,
} from "./api";
import { AppShell } from "./components/AppShell";
import { AgentWorkspace } from "./features/AgentWorkspace";
import { BetaOpsView } from "./features/BetaOpsView";
import { DeploymentsView } from "./features/DeploymentsView";
import { LoginView } from "./features/LoginView";
import { GovernanceView } from "./features/GovernanceView";
import { IncidentsView } from "./features/IncidentsView";
import { InsightsView } from "./features/InsightsView";
import { PlaceholderView } from "./features/PlaceholderView";
import { QodesView } from "./features/QodesView";
import type {
  AuthSession,
  IncidentItem,
  NotificationItem,
  QodeItem,
  TimelineEvent,
  ViewId,
  WorkspaceMembership,
} from "./types";

export default function App() {
  const [activeView, setActiveView] = useState<ViewId>("agent");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [session, setSession] = useState<AuthSession | null | undefined>(
    undefined,
  );
  const [qodes, setQodes] = useState<QodeItem[]>([]);
  const [incidents, setIncidents] = useState<IncidentItem[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [workspaceSwitching, setWorkspaceSwitching] = useState(false);
  const [dataError, setDataError] = useState("");
  const resolvableCount = qodes.filter(
    (qode) => qode.status === "active" || qode.status === "degraded",
  ).length;
  const unresolvedIncidentCount = incidents.filter(
    (incident) => incident.status !== "resolved",
  ).length;

  useEffect(() => {
    getCurrentSession()
      .then(setSession)
      .catch(() => setSession(null));
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(null);
      setQodes([]);
      setIncidents([]);
      setNotifications([]);
      setTimeline([]);
      setWorkspaces([]);
    };
    window.addEventListener("qode:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("qode:unauthorized", handleUnauthorized);
  }, []);

  useEffect(() => {
    if (!session) return;
    let active = true;
    Promise.all([
      getDashboard(),
      getIncidents(),
      getWorkspaces(),
      getNotifications(),
    ])
      .then(
        ([
          dashboard,
          incidentResponse,
          workspaceResponse,
          notificationResponse,
        ]) => {
        if (!active) return;
        setQodes(dashboard.qodes);
        setIncidents(incidentResponse.incidents);
        setTimeline(dashboard.events);
        setWorkspaces(workspaceResponse.workspaces);
        setNotifications(notificationResponse.notifications);
        setDataError("");
        },
      )
      .catch(() => {
        if (active) {
          setDataError("实时数据暂时不可用，请稍后重试。");
        }
      });
    return () => {
      active = false;
    };
  }, [session]);

  const upsertQode = (qode: QodeItem, event?: TimelineEvent | null) => {
    setQodes((current) => [
      qode,
      ...current.filter((item) => item.id !== qode.id),
    ]);
    if (event) {
      setTimeline((current) => [
        event,
        ...current.filter((item) => item.id !== event.id),
      ]);
    }
  };

  const upsertIncident = (incident: IncidentItem) => {
    setIncidents((current) => [
      incident,
      ...current.filter((item) => item.id !== incident.id),
    ]);
  };

  const handleLogin = async (username: string, password: string) => {
    const authenticated = await login(username, password);
    setSession(authenticated);
    return authenticated;
  };

  const handleRegister = async (input: {
    workspaceName: string;
    displayName: string;
    username: string;
    email: string;
    password: string;
    acceptTerms: true;
    termsVersion: "2026-07-24";
    privacyVersion: "2026-07-24";
    website: "";
  }) => {
    return register(input);
  };

  const handleLogout = async () => {
    await logout();
    setSession(null);
    setQodes([]);
    setIncidents([]);
    setNotifications([]);
    setTimeline([]);
    setWorkspaces([]);
    setAccountStateDefaults();
  };

  const setAccountStateDefaults = () => {
    setActiveView("agent");
    setMobileMenuOpen(false);
    setDataError("");
  };

  const handleChangePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    await changePassword(currentPassword, newPassword);
    setSession(null);
    setQodes([]);
    setIncidents([]);
    setNotifications([]);
    setTimeline([]);
    setWorkspaces([]);
    setAccountStateDefaults();
  };

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    if (!session || workspaceId === session.workspace.id) return;
    setWorkspaceSwitching(true);
    setDataError("");
    try {
      const nextSession = await switchWorkspace(workspaceId);
      setQodes([]);
      setIncidents([]);
      setNotifications([]);
      setTimeline([]);
      setSession(nextSession);
    } catch (switchError) {
      setDataError(
        switchError instanceof Error
          ? switchError.message
          : "Workspace 切换失败。",
      );
    } finally {
      setWorkspaceSwitching(false);
    }
  };

  const handleNotificationRead = async (notificationId: string) => {
    const response = await markNotificationRead(notificationId);
    setNotifications((current) =>
      current.map((item) =>
        item.id === response.notification.id ? response.notification : item,
      ),
    );
    if (response.notification.resource.type === "incident") {
      setActiveView("incidents");
    }
  };

  const refreshDashboard = async () => {
    const dashboard = await getDashboard();
    setQodes(dashboard.qodes);
    setTimeline(dashboard.events);
  };

  if (session === undefined) {
    return (
      <main className="session-loading">
        <span className="brand-loader" />
        正在恢复安全会话
      </main>
    );
  }

  if (session === null) {
    return (
      <LoginView
        onConfirmPasswordReset={confirmPasswordReset}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onRequestPasswordReset={requestPasswordReset}
        onResendVerification={resendVerification}
        onVerifyEmail={verifyEmail}
      />
    );
  }

  return (
    <AppShell
      activeView={activeView}
      mobileMenuOpen={mobileMenuOpen}
      incidentCount={unresolvedIncidentCount}
      notifications={notifications}
      qodeCount={qodes.length}
      resolvableCount={resolvableCount}
      session={session}
      workspaces={workspaces}
      workspaceSwitching={workspaceSwitching}
      onChangePassword={handleChangePassword}
      onLogout={handleLogout}
      onMobileMenuChange={setMobileMenuOpen}
      onNotificationRead={handleNotificationRead}
      onViewChange={setActiveView}
      onWorkspaceSwitch={handleWorkspaceSwitch}
    >
      {dataError ? <div className="system-banner">{dataError}</div> : null}
      {activeView === "agent" ? (
        <AgentWorkspace
          capabilities={session.capabilities}
          incidents={incidents}
          qodes={qodes}
          timeline={timeline}
          onQodeChanged={upsertQode}
          onOpenQodes={() => setActiveView("qodes")}
          onOpenIncidents={() => setActiveView("incidents")}
        />
      ) : null}
      {activeView === "qodes" ? (
        <QodesView
          capabilities={session.capabilities}
          qodes={qodes}
          onCreateQode={() => setActiveView("agent")}
          onQodeChanged={upsertQode}
        />
      ) : null}
      {activeView === "deployments" ? (
        <DeploymentsView
          capabilities={session.capabilities}
          qodes={qodes}
          workspaceId={session.workspace.id}
          onQodeChanged={upsertQode}
        />
      ) : null}
      {activeView === "incidents" ? (
        <IncidentsView
          capabilities={session.capabilities}
          incidents={incidents}
          workspaceId={session.workspace.id}
          onIncidentChanged={upsertIncident}
        />
      ) : null}
      {activeView === "insights" ? (
        <InsightsView
          capabilities={session.capabilities}
          qodes={qodes}
          workspaceId={session.workspace.id}
          onIncidentChanged={upsertIncident}
          onQodeChanged={upsertQode}
        />
      ) : null}
      {activeView === "beta" ? (
        <BetaOpsView
          capabilities={session.capabilities}
          qodes={qodes}
          workspaceId={session.workspace.id}
          onRefreshQodes={refreshDashboard}
        />
      ) : null}
      {activeView !== "agent" &&
      activeView !== "qodes" &&
      activeView !== "deployments" &&
      activeView !== "incidents" &&
      activeView !== "insights" &&
      activeView !== "beta" ? (
        activeView === "governance" ? (
          <GovernanceView
            qodes={qodes}
            session={session}
            onIncidentChanged={upsertIncident}
            onQodeChanged={upsertQode}
          />
        ) : (
          <PlaceholderView view={activeView} />
        )
      ) : null}
    </AppShell>
  );
}
